import React, {
  useContext, useEffect, useMemo, useRef
} from 'react';
import uniqueBy from 'lodash/uniqBy';
import startCase from 'lodash/startCase';
import Divider from '@components/shared/Divider/Divider';
import ImageGrid from '@components/pages/ProductV2/shared/components/ImageGrid/ImageGrid';
import ShowAt from '@components/shared/conditional/ShowAt';
import HideAt from '@components/shared/conditional/HideAt';
import ProductImageCarousel from '@components/pages/ProductV2/shared/components/ProductImageCarousel/ProductImageCarousel';
import ShareModalContext, { SHARETYPE } from '@context/Modal/share-modal-context';
import getProductVanityPath from '@lib/vendorVariant/getProductVanityPath';
import env from '@lib/envs/env';
import VendorVariantContext from '@context/VendorVariant/vendor-variant-context';
import { useMinBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import AccordionItem from '@components/shared/Accordion/AccordionItem/AccordionItem';
import { IAttributeFilterInput, IAttributeOption } from '@models/VendorVariant/i-vendor-product-group';
import { IProductAttribute, IVendorVariantInternal } from '@models/VendorVariant/i-vendor-variant';
import UserContext from '@context/User/user-context';
import { useTheme } from '@components/theme';
import classNames from 'classnames';
import useWindowSize from '@hooks/useWindowSize/useWindowSize';
import { attributeWhitelist } from '@lib/vendorVariant/productAttributeUtils';
import { Vendors } from '@components/shared/shop/model/Vendors';
import SaleBanner from '@components/shared/shop/ShopProductCard/SaleBanner/SaleBanner';
import SaleTreatmentBanner from '@components/shared/shop/SaleTreatmentBanner/SaleTreatmentBanner';
import { DesignAwardPlace } from '@components/shared/DesignAwards/DesignAwardBadge';
import ReadMoreTextBox from '@components/shared/ReadMoreTextBox/ReadMoreTextBox';
import PriceVerificationGuarantee from '../PriceMatchGuarantee/PriceVerificationGuarantee';
import ProductInfo from '../ProductInfo/ProductInfo';
import styles from './ProductDetails.module.scss';
import ChevronIcon from './assets/chevron.svg';
import SaveForLater from '../SaveProductForLater/SaveProductForLater';
import { IZoomableImage } from '../MobileImageZoom/MobileImageZoom';

interface IProductDetailsProps {
  busy?: boolean;
  attributeFilters?: React.ReactNode;
  selectedAttributeFilters?: IAttributeFilterInput[];
  vendorVariants?: IVendorVariantInternal[];
  onAttributeFilterChange?: (id: string, value: string) => void;
  isAttributeOptionSelected?: (option: IAttributeOption) => boolean;
  selectVendorVariant?: (vendorVariantId: number) => void;
  setShowStickyHeader: (state: boolean) => void;
  onClickReviews: () => void;
  setActiveZoomImage?: (image: IZoomableImage) => void;
  cartActions: any;
  handleSave?: () => void;
}

const ProductDetails = ({
  setActiveZoomImage,
  setShowStickyHeader,
  onClickReviews,
  attributeFilters,
  cartActions,
  handleSave
}: IProductDetailsProps) => {
  const { open: openShareModal } = useContext(ShareModalContext);
  const vendorVariant = useContext(VendorVariantContext);
  const addToCartRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMinBreakpoint('desktop');
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const { height: windowHeight } = useWindowSize();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inStock = vendorVariant.availability && (vendorVariant.availability.includes('in_stock') || vendorVariant.availability.includes('back_ordered'));
  const hasTieredDiscount = !!vendorVariant.vendorTieredDiscountCode;

  const handleShare = () => {
    if (vendorVariant) {
      openShareModal({
        modelId: Number(vendorVariant.id),
        shareType: SHARETYPE.PRODUCT,
        url: `https://${env.DOMAIN_NAME}${getProductVanityPath(vendorVariant)}`,
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const onWindowScroll = () => {
    if (addToCartRef.current && addToCartRef.current?.getBoundingClientRect().y <= 0) {
      setShowStickyHeader(true);
    } else {
      setShowStickyHeader(false);
    }
  };

  const filterWhitelistedAttributes = useMemo(() => {
    return uniqueBy(vendorVariant.attributeValues as IProductAttribute[], 'attributeType.title')
      .filter((attribute) => !!attributeWhitelist[attribute.attributeType.title])
      .map((attribute) => ({
        ...attribute,
        title: startCase(attribute.attributeType.title),
        attributeType: {
          ...attribute.attributeType,
          title: startCase(attribute.attributeType.title)
        }
      }));
  }, [vendorVariant.attributeValues]);

  useEffect(() => {
    if (isDesktop) {
      window.addEventListener('scroll', onWindowScroll);
    } else {
      window.removeEventListener('scroll', onWindowScroll);
    }

    return () => {
      window.removeEventListener('scroll', onWindowScroll);
    };
  }, [isDesktop]);

  const scrollTop = useMemo(() => {
    const scrollContainer = scrollRef.current;

    if (!scrollContainer) {
      return '';
    }

    return `-${scrollContainer.clientHeight / 4}px`;
  }, [windowHeight, scrollRef.current]);

  return (
    <div className={styles.ProductDetails} data-test="product-sidebar">

      {vendorVariant.isOnSale && inStock && (
        <SaleBanner
          className={styles.SaleBanner}
          backgroundColor={theme.colors.shade6}
          product={vendorVariant}
        />
      )}
      {hasTieredDiscount && inStock &&
        vendorVariant.vendorTieredDiscountCode && (
        <SaleTreatmentBanner
          vendorTieredDiscountCode={vendorVariant.vendorTieredDiscountCode}
          className={styles.SaleBanner}
        />
      )}
      {hasTieredDiscount && inStock &&
        vendorVariant.vendorTieredDiscountCode && (
        <SaleTreatmentBanner
          vendorTieredDiscountCode={vendorVariant.vendorTieredDiscountCode}
          className={styles.SaleBanner}
        />
      )}

      <SaveForLater handleSave={handleSave} className={styles.SaveForLater} />

      <ShowAt breakpoint="desktop">
        <ImageGrid
          images={vendorVariant.images}
          altText={vendorVariant.title}
          designAward={vendorVariant.designAward as DesignAwardPlace | undefined}
        />
      </ShowAt>
      <HideAt breakpoint="desktop">
        <ProductImageCarousel
          images={vendorVariant.images}
          altText={vendorVariant.title}
          setActiveZoomImage={setActiveZoomImage}
          designAwardPlace={vendorVariant.designAward as DesignAwardPlace | undefined}
        />
      </HideAt>
      <div className={styles.Product}>
        <div
          className={styles.ScrollContainer}
          ref={scrollRef}
          style={{
            top: scrollTop
          }}
        >
          <ProductInfo
            className={styles.ProductInfo}
            onShare={handleShare}
            onClickReviews={onClickReviews}
          />

          {attributeFilters}

          <div ref={addToCartRef} className={styles.AddToCartWrapper}>
            {cartActions}
            {(user?.role === 'designer' || user?.role === 'admin') && (
              <div
                className={styles.EditButton}
                data-test="edit-button"
              >
                <a
                  role="button"
                  className={classNames(
                    theme.styles.Button,
                    theme.styles.Secondary,
                    theme.styles.Small
                  )}
                  data-test="product-sidebar__edit-button"
                  rel="noopener noreferrer"
                  href={`/board-builder?query=${vendorVariant.id}`}
                  target="_blank"
                >
                  Edit Vendor Variant
                </a>
              </div>
            )}
            <PriceVerificationGuarantee className={styles.PriceMatch} />
            <Divider className={styles.DividerTop} />
          </div>

          <AccordionItem
            title="Description"
            className={styles.Details}
            titleClassName={styles.AccordionTitle}
            headerButtonClass={styles.AccordionHeader}
            iconClassName={styles.AccordionIcon}
            showDivider
            showIcon
          >
            {vendorVariant.description && (
            <ReadMoreTextBox
              className={styles.ProductDescription}
              maxCharThreshold={276}
              textContent={vendorVariant.description}
            />
            )}
          </AccordionItem>

          <AccordionItem
            title="Dimensions & Details"
            className={styles.Details}
            titleClassName={styles.AccordionTitle}
            headerButtonClass={styles.AccordionHeader}
            iconClassName={styles.AccordionIcon}
            showDivider
            showIcon
            openedInitialState
          >
            <ul className={styles.DetailsList}>
              {filterWhitelistedAttributes.map((attribute) => (
                <li key={attribute.attributeType.id}>
                  <strong>{attribute.attributeType.title}:&nbsp;</strong>
                  <span>{attribute.value}</span>
                </li>
              ))}

              <InteriorDefineCustomizeLink
                weblink={vendorVariant.weblink ?? undefined}
                vendorId={vendorVariant.vendor?.id}
                color={theme.colors.tint3}
              />

            </ul>
          </AccordionItem>

          <AccordionItem
            title="Shipping & Returns"
            className={styles.Details}
            titleClassName={styles.AccordionTitle}
            headerButtonClass={styles.AccordionHeader}
            iconClassName={styles.AccordionIcon}
            showDivider
            showIcon
          >
            <div className={styles.ShippingWrapper}>
              <strong>Shipping:&nbsp;</strong>
              {vendorVariant.shippingPolicy ? (
                <>
                  {vendorVariant.shippingPolicy.description && (
                    <div className={styles.ShippingPolicyWrapper}>
                      {vendorVariant.shippingPolicy.description}
                    </div>
                  )}
                  <div className={styles.ShippingPolicyWrapper}>
                    <a href={vendorVariant.shippingPolicy.url} target="_blank">
                      {vendorVariant.shippingPolicy.urlText || 'See Shipping Policy Details'}
                    </a>
                  </div>
                </>
              ) : 'Information not available'}
            </div>
            {vendorVariant.etaText && (
              <div className={styles.ShippingWrapper}>
                <strong>Estimated Arrival:&nbsp;</strong>
                {vendorVariant.etaText}
              </div>
            )}
            <div className={styles.ShippingWrapper}>
              <strong>Return Policy Details:&nbsp;</strong>
              {vendorVariant.returnPolicy?.description && (
                <div className={styles.ReturnPolicyWrapper}>
                  {vendorVariant.returnPolicy?.description}
                </div>
              )}
              <div className={styles.ReturnPolicyWrapper}>
                {vendorVariant.returnPolicy?.url ? (
                  <>
                    <a href={vendorVariant.returnPolicy.url} target="_blank">
                      {'See Return Policy Details'}
                    </a>
                    <ChevronIcon />
                  </>
                ) : 'Information not available'}
              </div>
            </div>
          </AccordionItem>
        </div>
      </div>
    </div>
  );
};

interface InteriorDefineCustomizeLinkProps {
  weblink?: string;
  vendorId: string;
  color: string;
}

function InteriorDefineCustomizeLink({
  weblink, vendorId, color
}: InteriorDefineCustomizeLinkProps) {
  if (!weblink || Number(vendorId) !== Vendors.INTERIOR_DEFINE) {
    return null;
  }

  return (
    <div>
      Want more options?{' '}
      <a href={weblink} style={{ color }} target="_blank">
        Customize on Interior Define
      </a>
    </div>
  );
}

export default ProductDetails;
